// File: frontend/src/pages/staff/Payroll.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/axios';

const Payroll = () => {
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());

  const [payrollList, setPayrollList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Generate Payroll Modal State
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [staffOptions, setStaffOptions] = useState([]);
  const [selectedStaffId, setSelectedStaffId] = useState('');
  const [basicSalary, setBasicSalary] = useState('');
  const [allowances, setAllowances] = useState('0');
  const [deductions, setDeductions] = useState('0');
  const [paymentMethod, setPaymentMethod] = useState('bank_transfer');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);

  const [submitting, setSubmitting] = useState(false);
  const [modalError, setModalError] = useState(null);

  /**
   * Fetch payroll list for selected month and year
   */
  const fetchPayroll = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get('/api/v1/staff/payroll', {
        params: { month, year }
      });

      if (response.data?.success) {
        setPayrollList(response.data.data || []);
      } else {
        setError(response.data?.message || 'Failed to load payroll list.');
      }
    } catch (err) {
      console.error('Error fetching payroll list:', err);
      setError(err.response?.data?.message || err.message || 'Error loading payroll.');
    } finally {
      setLoading(false);
    }
  }, [month, year]);

  useEffect(() => {
    fetchPayroll();
  }, [fetchPayroll]);

  /**
   * Fetch active staff for payroll dropdown selection
   */
  const fetchStaffOptions = async () => {
    try {
      const response = await api.get('/api/v1/staff', {
        params: { status: 'active', limit: 100 }
      });
      if (response.data?.success) {
        const list = response.data.data || [];
        setStaffOptions(list);
        if (list.length > 0) {
          setSelectedStaffId(String(list[0].id));
          setBasicSalary(String(list[0].salary || 0));
        }
      }
    } catch (err) {
      console.error('Error fetching staff list for payroll:', err);
    }
  };

  const handleOpenGenerateModal = () => {
    fetchStaffOptions();
    setShowGenerateModal(true);
  };

  /**
   * Handle changing staff member in modal
   */
  const handleStaffSelectionChange = (stId) => {
    setSelectedStaffId(stId);
    const stObj = staffOptions.find((s) => String(s.id) === String(stId));
    if (stObj) {
      setBasicSalary(String(stObj.salary || 0));
    }
  };

  /**
   * Submit generate payroll form
   */
  const handleGeneratePayroll = async (e) => {
    e.preventDefault();
    if (!selectedStaffId) {
      setModalError('Please select a staff member.');
      return;
    }

    try {
      setSubmitting(true);
      setModalError(null);

      const payload = {
        staff_id: selectedStaffId,
        month: Number(month),
        year: Number(year),
        basic_salary: parseFloat(basicSalary) || 0,
        allowances: parseFloat(allowances) || 0,
        deductions: parseFloat(deductions) || 0,
        payment_date: paymentDate,
        payment_method: paymentMethod,
        status: 'paid'
      };

      const response = await api.post('/api/v1/staff/payroll', payload);

      if (response.data?.success) {
        setShowGenerateModal(false);
        fetchPayroll();
      } else {
        setModalError(response.data?.message || 'Failed to generate payroll entry.');
      }
    } catch (err) {
      console.error('Error generating payroll:', err);
      setModalError(err.response?.data?.message || err.message || 'Failed to generate payroll entry.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Top Navbar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/staff" className="w-10 h-10 rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200 flex items-center justify-center font-bold transition">
              ←
            </Link>
            <div>
              <h1 className="font-bold text-slate-800 text-lg leading-tight">Monthly Payroll & Salaries</h1>
              <p className="text-xs text-slate-500">Generate payroll records & print employee salary slips</p>
            </div>
          </div>

          <button
            onClick={handleOpenGenerateModal}
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-4 py-2.5 rounded-xl text-sm shadow-sm transition flex items-center gap-2"
          >
            <span className="text-base">+</span> Generate Payroll Entry
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        
        {/* Month/Year Filter Selection */}
        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <h2 className="font-bold text-slate-800 text-base">Select Payroll Period</h2>

          <div className="flex items-center gap-3 w-full sm:w-auto">
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold"
            >
              {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => (
                <option key={m} value={m}>Month {m}</option>
              ))}
            </select>

            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold"
            >
              <option value={2025}>2025</option>
              <option value={2026}>2026</option>
              <option value={2027}>2027</option>
            </select>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="p-4 bg-red-50 border-l-4 border-red-500 text-red-800 rounded-xl text-sm flex items-center justify-between shadow-sm">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="font-bold text-red-600">✕</button>
          </div>
        )}

        {/* Payroll Table Card */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-slate-100 flex items-center justify-between">
            <h3 className="font-bold text-slate-900 text-base">Payroll Register ({month}/{year})</h3>
            <span className="text-xs text-slate-500">Total Entries: {payrollList.length}</span>
          </div>

          {loading ? (
            <div className="p-12 text-center text-slate-400 font-medium">Loading payroll records...</div>
          ) : payrollList.length === 0 ? (
            <div className="p-12 text-center text-slate-400 font-medium">
              No payroll entries generated for Month {month}, {year}. Click "+ Generate Payroll Entry" to record salaries.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs font-sans">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-slate-500 uppercase font-semibold">
                    <th className="py-3.5 px-6">Staff Code</th>
                    <th className="py-3.5 px-6">Staff Name</th>
                    <th className="py-3.5 px-4 text-right">Basic Salary</th>
                    <th className="py-3.5 px-4 text-right">Allowances</th>
                    <th className="py-3.5 px-4 text-right">Deductions</th>
                    <th className="py-3.5 px-4 text-right">Net Salary</th>
                    <th className="py-3.5 px-4 text-center">Status</th>
                    <th className="py-3.5 px-6 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-700 font-medium">
                  {payrollList.map((p) => (
                    <tr key={p.id} className="hover:bg-slate-50/80 transition">
                      <td className="py-4 px-6 font-mono font-bold text-primary">
                        {p.staff_code || `ID-${p.staff_id}`}
                      </td>
                      <td className="py-4 px-6 font-bold text-slate-900 text-sm">
                        {p.name}
                        <span className="block text-[11px] text-slate-400 font-normal">{p.designation}</span>
                      </td>
                      <td className="py-4 px-4 text-right font-mono text-slate-700">
                        Rs. {parseFloat(p.basic_salary).toLocaleString(undefined, { minimumFractionDigits: 0 })}
                      </td>
                      <td className="py-4 px-4 text-right font-mono text-emerald-600 font-semibold">
                        +Rs. {parseFloat(p.allowances).toLocaleString(undefined, { minimumFractionDigits: 0 })}
                      </td>
                      <td className="py-4 px-4 text-right font-mono text-red-600 font-semibold">
                        -Rs. {parseFloat(p.deductions).toLocaleString(undefined, { minimumFractionDigits: 0 })}
                      </td>
                      <td className="py-4 px-4 text-right font-mono font-extrabold text-slate-900">
                        Rs. {parseFloat(p.net_salary).toLocaleString(undefined, { minimumFractionDigits: 0 })}
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold capitalize ${
                          p.status === 'paid' ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' : 'bg-amber-100 text-amber-800 border border-amber-300'
                        }`}>
                          {p.status}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-right">
                        <Link
                          to={`/staff/payroll/${p.staff_id}/slip?month=${month}&year=${year}`}
                          className="bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-bold px-3 py-1.5 rounded-lg border border-indigo-200 transition text-xs inline-block"
                        >
                          Salary Slip
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

        </div>
      </main>

      {/* Generate Payroll Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-slate-200">
            <div className="flex items-center justify-between border-b pb-3 mb-4">
              <h3 className="font-bold text-slate-800 text-base">Generate Staff Payroll Entry</h3>
              <button onClick={() => setShowGenerateModal(false)} className="text-slate-400 hover:text-slate-600 font-bold">✕</button>
            </div>

            {modalError && (
              <div className="mb-4 p-3 bg-red-50 text-red-700 border border-red-200 rounded-xl text-xs">
                {modalError}
              </div>
            )}

            <form onSubmit={handleGeneratePayroll} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">Select Staff Member *</label>
                <select
                  value={selectedStaffId}
                  onChange={(e) => handleStaffSelectionChange(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs"
                >
                  {staffOptions.map((st) => (
                    <option key={st.id} value={st.id}>
                      {st.name} ({st.staff_id}) - {st.designation}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">Basic Salary (Rs.)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={basicSalary}
                    onChange={(e) => setBasicSalary(e.target.value)}
                    required
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-mono font-bold"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">Allowances (Rs.)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={allowances}
                    onChange={(e) => setAllowances(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-mono"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">Deductions (Rs.)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={deductions}
                    onChange={(e) => setDeductions(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-mono text-red-600"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">Payment Method</label>
                  <select
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs capitalize"
                  >
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="cash">Cash</option>
                    <option value="cheque">Cheque</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">Payment Date</label>
                <input
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs"
                />
              </div>

              <div className="pt-2 flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowGenerateModal(false)}
                  className="flex-1 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl text-xs transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl text-xs transition disabled:opacity-50"
                >
                  {submitting ? 'Processing...' : 'Submit Payroll'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default Payroll;
